import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ConstantsService} from '../../fidelidade/ConstantsService';
import {ehCelular, EstadoOnboardingPainel, OnboardingPainelService} from '../onboarding-painel.service';
import {DESCRICOES_DAS_FUNCOES, FUNCOES_POR_FASE, NOMES_DAS_FASES, QUANDO_CHEGA}
  from '../../../../server/lib/onboarding/FasesDoMenuOnboarding';

/** Nome curto de cada função do menu, para o cartão de novidades (o catálogo do menu tem o nome completo). */
const NOMES: Record<string, string> = {
  'index': 'Início', 'pedidos': 'Pedidos', 'pedidos/novo-v3': 'Novo pedido', 'pdv': 'PDV', 'adicionais-v2': 'Adicionais',
  'produtos-v2/novo': 'Novo produto', 'loja/tab/impressao': 'Impressão', 'fila-impressao': 'Fila de impressão', 'loja/tab/fotos': 'Fotos da loja',
  'temas': 'Temas', 'loja/tab/pausasprogramadas': 'Pausas programadas', 'loja/numeros-whatsapp': 'Números WhatsApp',
  'contatos': 'Clientes', 'contatos/novo': 'Novo cliente', 'notificacoes': 'Mensagens automáticas', 'avaliacoes': 'Avaliações',
  'mensagens': 'Histórico de mensagens', 'financeiro-delivery': 'Vendas de delivery', 'relatorios': 'Relatórios',
  'insights2': 'Análise do cardápio', 'fidelidade/pontuacoes': 'Fidelidade', 'loja/tab/fidelidade': 'Regras de fidelidade',
  'marketing': 'Campanhas WhatsApp', 'nova-campanha': 'Nova campanha', 'cupons': 'Cupons', 'promocoes': 'Promoções',
  'entregadores': 'Entregadores', 'pedidos/analise': 'Mapa de entregas', 'contatos-kanban': 'Quadro de clientes',
  'contatos-fila': 'Fila de clientes', 'financeiro-balcao': 'Vendas de balcão', 'taxas': 'Taxas calculadas', 'banner': 'Banners',
  'vitrines': 'Vitrines', 'tempo-real': 'Cardápio em tempo real'
};

/**
 * Evolução da loja no Início, depois dos primeiros passos: a barra das quatro fases (a mesma do menu que cresce),
 * o que chegou de novo no menu, o que falta para avançar e o que vem aí.
 */
@Component({
  selector: 'app-evolucao-loja',
  templateUrl: './evolucao-loja.component.html',
  styleUrls: ['./evolucao-loja.component.scss']
})
export class EvolucaoLojaComponent implements OnInit, OnDestroy {
  estado: EstadoOnboardingPainel | null = null;
  empresa: any = {};
  equipamentosAbertos = false;
  /** extensão e impressora só instalam no computador */
  readonly celular = ehCelular();
  copiado = false;
  readonly fases = [1, 2, 3, 4];
  private destruir$ = new Subject<void>();
  private carregado = false;

  constructor(private onboarding: OnboardingPainelService, private constants: ConstantsService) {}

  ngOnInit(): void {
    this.constants.empresa$.pipe(takeUntil(this.destruir$)).subscribe(empresa => {
      this.empresa = empresa || {};
      if (this.empresa?.onboarding && !this.empresa.onboarding.ativo && !this.carregado) this.carregue();
    });
  }

  ngOnDestroy(): void {
    this.destruir$.next();
    this.destruir$.complete();
  }

  carregue(): void {
    this.carregado = true;
    this.onboarding.estado().then(estado => this.estado = estado?.ativo === false && estado.evolucao ? estado : null).catch(() => {});
  }

  get visivel(): boolean { return !!this.estado?.evolucao; }

  nomeFase(f: number): string { return (NOMES_DAS_FASES as any)[f]; }

  preenchimento(f: number): number {
    const pct = this.estado?.evolucao?.pct || 0, atual = this.estado?.evolucao?.fase || 1;
    if (f < atual) return 100;
    if (f > atual) return 0;
    return Math.max(4, Math.min(100, (pct - (f - 1) * 25) * 4));
  }

  get novas(): Array<{id: string; nome: string; descricao: string}> {
    return (this.estado?.menu?.novas || []).filter(id => NOMES[id]).map(id => ({id, nome: NOMES[id], descricao: DESCRICOES_DAS_FUNCOES[id] || ''}));
  }

  rota(id: string): string { return id === 'pdv' ? '/admin/pedidos' : `/admin/${id}`; }

  dispenseNovidades(): void {
    if (this.estado?.menu) this.estado.menu.novas = [];
    this.onboarding.marqueVista('todas').then(() => this.constants.recarregueEmpresa()).catch(() => {});
  }

  abriuNovidade(id: string): void {
    if (this.estado?.menu) this.estado.menu.novas = this.estado.menu.novas.filter(n => n !== id);
    this.onboarding.marqueVista(id).catch(() => {});
  }

  /** A próxima leva do menu e quando ela chega. */
  get vemAi(): {quando: string; nomes: string} | null {
    const fase = (this.estado?.evolucao?.fase || 1) + 1;
    if (fase > 4 || this.estado?.menu?.menuCompleto) return null;
    const ids = (FUNCOES_POR_FASE as any)[fase] as string[];
    return {quando: (QUANDO_CHEGA as any)[fase], nomes: ids.filter(id => NOMES[id]).slice(0, 4).map(id => NOMES[id]).join(' · ')};
  }

  get pendeEquipamento(): boolean {
    const p = this.estado?.evolucao?.proximas || [];
    return p.some(x => /extensão|impressora/i.test(x));
  }

  get pendeDivulgar(): boolean { return (this.estado?.evolucao?.proximas || []).some(x => /divulgar/i.test(x)); }

  copieLink(): void {
    const link = String(this.empresa?.linkLoja || '');
    if (!link) return;
    const feito = () => {
      this.copiado = true;
      setTimeout(() => this.copiado = false, 1800);
      this.onboarding.confirme('link-divulgado').then(estado => this.estado = estado).catch(() => {});
    };
    try { navigator.clipboard.writeText(link).then(feito, feito); } catch (e) { feito(); }
  }

  atualize(estado: EstadoOnboardingPainel): void { if (estado?.evolucao) this.estado = estado; }
}
